"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { isEmpty, isString } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import { Tooltip } from "../Tooltip/Tooltip";
import { BaseInputField } from "../BaseInputField/BaseInputField";
import { baseInputFieldStyle, positionerStyle } from "./ToggleInput.css";
import { mergeRefs } from "../../utils";
import { TargetPositioner } from "../TargetPositioner/TargetPositioner";
import { Backdrop } from "../Backdrop/Backdrop";
import { Portal } from "../Portal/Portal";
import {
  ToggleInputFieldContainer,
  type ToggleInputFieldContainerProps,
} from "./components/ToggleInputFieldContainer";
import { useDynamicFormInput } from "../../hooks/useDynamicFormInput";
import type { DynamicInputValidationResult } from "../../hooks/useDynamicInputValidation";
import { useInitializedEffect } from "../../hooks/useInitializedEffect";
import { useUuid } from "../../hooks/useUuid";
import { useOnce } from "../../hooks/useOnce";
import type { FormInputProps } from "../FormInput/FormInput";
import type { Timeout } from "../../types";
import {
  InputConfiguration,
  ToggleInputDisplayConfiguration,
  toggleInputTextProps,
} from "../../services/inputs";
import { Text } from "../Text";
import { TruncatedText } from "../TruncatedText";
import type { TextAlignProp } from "../../props";
import { FormInputError } from "../FormInputError";

export type ToggleInputProps = FormInputProps & {
  onStartEditing?: () => void;
  onStopEditing?: () => void;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * A toggle input field that allows the user to edit the value of the field.
 */
export const ToggleInput: FC<ToggleInputProps> = ({
  id,
  type = "string",
  disabled = false,
  success = false,
  error,
  label,
  value,
  highlight = false,
  selectOptions,
  onChange,
  onValidation,
  onStartEditing,
  onStopEditing,
  ref,
}) => {
  const { t } = useTranslation();
  const uuid = useUuid(id);

  const fieldRef = useRef<HTMLDivElement>(null);
  const timeout = useRef<Timeout>(undefined);
  const hasBeenEditedOnce = useRef(false);

  const [isPositionerActive, setPositionerActive] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [bounding, setBounding] = useState<DOMRect>();
  const [validation, setValidation] =
    useState<DynamicInputValidationResult | null>(null);

  const { parsedType, isInvalidType } = useDynamicFormInput({
    type,
    value,
  });

  useInitializedEffect(() => {
    if (isEditing) {
      onStartEditing?.();
    } else if (!isEditing && hasBeenEditedOnce.current) {
      onStopEditing?.();
    }
  }, [isEditing]);

  const edit = () => {
    if (disabled) {
      return;
    }

    // console.log("[ToggleInput] ▶️ Start editing", type);

    // console.log("[ToggleInput] ▶️ Start editing", type);
    clearTimeout(timeout.current);
    setBounding(fieldRef.current?.getBoundingClientRect());
    setIsEditing(true);
    hasBeenEditedOnce.current = true;
    setPositionerActive(true);
  };

  const stopEditing = () => {
    // console.log("[ToggleInput] 🛑 Stop editing", type);
    setIsEditing(false);
    timeout.current = setTimeout(() => setPositionerActive(false), 400);
  };

  useEffect(() => () => clearTimeout(timeout.current), []);

  const handleValidation: ToggleInputFieldContainerProps["onValidation"] = (
    result,
  ) => {
    setValidation(result);
    onValidation?.(result);
  };

  const handleClose = () => {
    // console.log("[ToggleInput] Handle close");
    stopEditing();
  };

  const handleCancel = () => {
    // console.log("[ToggleInput] Handle cancel");
    stopEditing();
  };

  const handleClickOutside = () => {
    stopEditing();
  };

  const plainDisplay = (() => {
    const display =
      ToggleInputDisplayConfiguration?.[type]?.display?.(value, {
        selectOptions,
        i18n: t,
      }) ?? undefined;

    if (!isEmpty(display)) {
      return display;
    }
    if (isEmpty(value)) {
      return <Text {...toggleInputTextProps}>-</Text>;
    }
    if (isString(value)) {
      return (
        <TruncatedText
          {...toggleInputTextProps}
          maxSize={24}
          value={value as string}
        />
      );
    }

    return <Text {...toggleInputTextProps}>{value}</Text>;
  })();

  useOnce(() => {
    if (!id) {
      console.warn(`ID is required for ToggleInput ${type}`);
    }
  }, [id]);

  if (!ToggleInputDisplayConfiguration?.[type]) {
    console.warn(
      `[ToggleInput] No display configuration found for type ${type} (skipping)`,
    );
    return null;
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      edit();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <>
      <motion.div
        layout
        transition={{ duration: 0.2 }}
        animate={{ width: isPositionerActive ? bounding?.width : "auto" }}
      >
        <BaseInputField
          ref={mergeRefs(ref, fieldRef)}
          id={uuid}
          label={label}
          variant="toggle"
          onClick={edit}
          tabIndex={0}
          leading={ToggleInputDisplayConfiguration?.[type]?.leading}
          trailing={ToggleInputDisplayConfiguration?.[type]?.trailing}
          active={isEditing && !disabled}
          error={(!!error || validation?.success === false) && !disabled}
          align={(InputConfiguration?.[type]?.props as TextAlignProp)?.align}
          disabled={disabled}
          highlight={highlight}
          success={isEditing ? undefined : success}
          data-field-key={id}
          className={baseInputFieldStyle}
          onKeyDown={handleKeyDown}
        >
          {plainDisplay}
        </BaseInputField>
      </motion.div>

      {!disabled && (
        <TargetPositioner
          targetRef={fieldRef}
          active={isPositionerActive}
          className={positionerStyle}
          placement="top"
          mode="fixed"
          content={
            <AnimatePresence>
              {isEditing && (
                <ToggleInputFieldContainer
                  id={uuid}
                  type={parsedType}
                  bounding={bounding}
                  isInvalidType={isInvalidType}
                  label={label}
                  value={value}
                  highlight={highlight}
                  selectOptions={selectOptions}
                  onChange={onChange}
                  onClose={handleClose}
                  onCancel={handleCancel}
                  onOutsideClick={handleClickOutside}
                  onValidation={handleValidation}
                  error={error}
                  success={success}
                />
              )}
            </AnimatePresence>
          }
        />
      )}
      {!disabled && (
        <Tooltip targetRef={fieldRef} value={t("general.inputChange")} />
      )}
      {!disabled && (
        <Portal>
          {isEditing && (
            <AnimatePresence>
              <Backdrop delay={0.1} />
            </AnimatePresence>
          )}
        </Portal>
      )}
      {!!error && !isEditing && (
        <FormInputError
          targetRef={fieldRef}
          message={error}
          floating
          placement="bottom"
        />
      )}
    </>
  );
};
